import * as React from "react"
import { cn } from "@/lib/utils"
import { ProductImage } from "./ui/ProductImage"
import { Button } from "./ui/button"
import { EmailIcon, AlertIcon } from "./icons"

export interface FraudulentItemErrorProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Product title (e.g., "NFC XP")
   * Optional as fraudulent items may not have product info
   */
  productTitle?: string
  /**
   * Product image URL
   * Optional as fraudulent items may not have product info
   */
  productImage?: string
  /**
   * NFC ID to display (read-only)
   * Optional as URL parsing may have failed
   */
  nfcId?: string
  /**
   * Error message to display
   * @default "This item could not be authenticated and look fraudulent. Don't hesitate to contact us to report it"
   */
  errorMessage?: string
  /**
   * Contact email address
   * @default "contact@anyw3ar.com"
   */
  contactEmail?: string
}

/**
 * Fraudulent Item Error component
 *
 * Displays an error message when an NFC signature verification fails (counterfeit detection).
 * Shows a warning to users that the item may be fraudulent and directs them to report it.
 *
 * This component appears when the backend returns 403 Forbidden for invalid NFC signatures.
 *
 * @example
 * ```tsx
 * <FraudulentItemError
 *   productTitle="NFC XP"
 *   productImage="/images/jacket.jpg"
 *   nfcId="adnoa46z5vr1z35ef"
 *   errorMessage="This item could not be authenticated and look fraudulent. Don't hesitate to contact us to report it"
 *   contactEmail="contact@anyw3ar.com"
 * />
 * ```
 */
export const FraudulentItemError = React.forwardRef<
  HTMLDivElement,
  FraudulentItemErrorProps
>(
  (
    {
      productTitle,
      productImage,
      nfcId,
      errorMessage = "This item could not be authenticated and look fraudulent. Don't hesitate to contact us to report it",
      contactEmail = "contact@anyw3ar.com",
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-8 items-center p-8 box-border",
          className
        )}
        {...props}
      >
        {/* Title */}
        {productTitle && (
          <div className="flex gap-4 items-center w-full">
            <p className="font-montserrat font-bold text-[0.9375rem] text-gray-900 text-center uppercase">
              Authentication Failed: {productTitle}
            </p>
          </div>
        )}

        {/* Product Image (if available) */}
        {productImage && (
          <div className="h-[12.6875rem] w-[11.9375rem] relative shrink-0">
            <ProductImage
              src={productImage}
              alt={productTitle || "Product"}
              containerClassName="absolute inset-0"
            />
          </div>
        )}

        {/* NFC ID Display with Error Icon (if available) */}
        {nfcId && (
          <div className="bg-white border border-gray-100 border-solid flex h-12 items-center justify-between px-4 rounded-[0.3125rem] w-full">
            <div className="flex flex-col gap-1 items-start w-[12.0625rem]">
              <div className="flex flex-col justify-end text-xs">
                <p className="font-montserrat font-normal leading-5 text-gray-500">
                  NFC ID :
                </p>
              </div>
              <div className="flex flex-col justify-end text-[1.125rem] tracking-[0.25rem]">
                <p className="font-montserrat font-normal leading-5 text-gray-500 whitespace-nowrap">
                  {nfcId}
                </p>
              </div>
            </div>
            <div className="flex gap-4 items-start px-0 py-2">
              <AlertIcon
                size={12}
                weight="bold"
                className="text-brand-red"
              />
            </div>
          </div>
        )}

        {/* Error Message */}
        <div className="flex flex-col gap-4 items-start w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-1 flex-col font-montserrat font-normal justify-end leading-[0] min-h-px min-w-px text-[0.9375rem] text-gray-500">
              <p className="leading-5 whitespace-pre-wrap">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Button */}
        <a href={`mailto:${contactEmail}?subject=Fraudulent Item Report`}>
          <Button
            variant="primary-dark"
            size="primary-dark"
            leftIcon={<EmailIcon size={12} />}
            className="h-10 w-[19.4375rem] rounded-[0.375rem] uppercase"
          >
            Report Fraudulent Item
          </Button>
        </a>
      </div>
    )
  }
)

FraudulentItemError.displayName = "FraudulentItemError"
